import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Text,
    useToast,
    VStack,
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Spinner,
    Image,
    Heading,
    Container,
    Center,
    useBreakpointValue, Button, Link
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import logo from './High res brand face logo.png'; // You'll need to add your logo file here
import moment from "moment";



export const SuccessRegistration = () => {
    const { registrationID } = useParams();
    const [registrationData, setRegistrationData] = useState(null);
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [clickCount, setClickCount] = useState(0);
    const [showBackdoor, setShowBackdoor] = useState(false);
    
    // Responsive values
    const logoSize = useBreakpointValue({ base: "80px", md: "120px" });
    const headingSize = useBreakpointValue({ base: "lg", md: "lg" });
    const mainFontSize = useBreakpointValue({ base: "md", md: "lg" });
    const detailsFontSize = useBreakpointValue({ base: "sm", md: "md" });
    const cardPadding = useBreakpointValue({ base: 10, md: 10 });
    const containerMaxW = useBreakpointValue({ base: "100%", md: "container.md" });

    // Calculate payment eligibility
    const getPaymentEligibility = () => {
        if (!registrationData?.start_date) return null;
        
        const startDate = moment(registrationData.start_date);
        const today = moment();
        const daysUntilEligible = startDate.diff(today, 'days');
        
        // Backdoor override for testing
        if (showBackdoor) {
            return {
                canPayNow: true,
                daysLeft: 0,
                daysUntilCourse: daysUntilEligible,
                skillsFutureAvailableDate: startDate.subtract(60, 'days')
            };
        }
        
        return {
            canPayNow: daysUntilEligible <= 60,
            daysLeft: daysUntilEligible > 60 ? daysUntilEligible - 60 : 0,
            daysUntilCourse: daysUntilEligible,
            skillsFutureAvailableDate: startDate.subtract(60, 'days')
        };
    };

    // Handle title click for backdoor
    const handleTitleClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);
        
        if (newCount === 5) {
            setShowBackdoor(!showBackdoor);
            setClickCount(0);
            toast({
                title: showBackdoor ? "Backdoor Deactivated" : "Backdoor Activated",
                description: showBackdoor ? "Showing normal SkillsFuture eligibility" : "Showing SkillsFuture as available",
                status: "info",
                duration: 2000,
                isClosable: true,
                position: "top",
            });
        }
    };

    useEffect(() => {
        const fetchRegistration = async () => {
          setLoading(true);
          try {
            const { data, error } = await supabase
              .from("full_registration_details_with_enrollment")
              .select(
                "id, participant_name, participant_email, name, start_date, end_date, price, course_run_id, draft_course_run_code, registration_type, registration_stage, billing_type, birth_age, nationality"
              )
              .gte("start_date", new Date().toLocaleDateString('fr-CA'))
              .eq("id", registrationID)
              .single();
    
            if (error) throw error;

            // Fetch sessions for the course run
            const courseRunCode = data.draft_course_run_code || data.course_run_id;
            let course_dates = [];
            if (courseRunCode) {
              const { data: sessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .eq("course_run_code", courseRunCode)
                .order("session_date", { ascending: true });

              if (!sessionsError && sessions) {
                course_dates = sessions.map(session => ({
                  date: session.session_date,
                  start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                  end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                  room: session.custom_room || session.room_id,
                  venue: session.custom_venue || session.venue_id
                }));
              }
            }
    
            setRegistrationData({
              ...data,
              course_dates: course_dates
            });
          } catch (error) {
            console.error("Error fetching registration:", error);
            toast({
              title: "Error",
              description: "Could not load registration details.",
              status: "error",
              isClosable: true,
              position: "top",
            });
          } finally {
            setLoading(false);
          }
        };
    
        fetchRegistration();
      }, [registrationID, toast]);

    return (
        <Box 
            w="100%"
            bg="gray.50"
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={{ base: 8, md: 12 }}
            px={{ base: 4, md: 8 }}
        >
            {loading ? (
                <Center h="60vh">
                    <Spinner 
                        size="xl" 
                        thickness="4px" 
                        speed="0.65s" 
                        color="brand.600"
                    />
                </Center>
            ) : (
                <Box maxW={containerMaxW}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <Box 
                            bg="white" 
                            p={cardPadding}
                            borderRadius="lg" 
                            boxShadow="md"
                        >
                            <VStack spacing={{base: 4, md: 8}}>
                                <motion.div
                                    initial={{y: -50}}
                                    animate={{y: 0}}
                                    transition={{delay: 0.2, duration: 0.8, ease: "easeOut"}}
                                >
                                    <Heading
                                        size={"sm"}
                                        color="brand.700"
                                        borderBottom="2px solid"
                                        borderColor="brand.400"
                                        pb={2}
                                        onClick={handleTitleClick}
                                        userSelect="none"
                                    >
                                        Registration received
                                    </Heading>
                                </motion.div>

                                <motion.div
                                    initial={{opacity: 0}}
                                    animate={{opacity: 1}}
                                    transition={{delay: 0.5, duration: 1, ease: "easeOut"}}
                                >
                                    <Text
                                        fontSize={"md"}
                                        color="brand.500"
                                        lineHeight="1.6"
                                    >
                                        {registrationData.registration_type === "Company-Sponsored" ? (
                                            <>
                                                Please give us some time to review your application. <br></br>You will
                                                receive a response at{" "}
                                                <Text
                                                    as="span"
                                                    color="orange.500"
                                                    fontWeight="600"
                                                >
                                                    {registrationData.participant_email}
                                                </Text>
                                                {" "}soon!
                                            </>
                                        ) : (
                                            <VStack spacing={4} alignItems={"center"}>
                                                <Text>
                                                    Please verify that you have received an enrollment pending email at {<Text
                                                    as={"span"} fontWeight={"600"}
                                                    color="teal.500" textDecoration={"underline"}>{registrationData.participant_email}</Text>}  before proceeding.
                                                    To confirm your enrollment, please proceed to review the student contract and make payment by
                                                    clicking on the button below.
                                                </Text>
                                                <Text
                                                    textAlign={"start"}
                                                    fontSize={"sm"}
                                                    color="brand.700"
                                                    fontWeight="700"
                                                >
                                                    Note: Skillsfuture payment can only be used less than 60 days before the course
                                                    start date.
                                                </Text>
                                                
                                                {/* Payment Eligibility Message */}
                                                {(() => {
                                                    const eligibility = getPaymentEligibility();
                                                    if (!eligibility) return null;
                                                    
                                                    if (eligibility.canPayNow) {
                                                        return (
                                                            <Text
                                                                fontSize={"sm"}
                                                                color="green.600"
                                                                fontWeight="600"
                                                            >
                                                                ✅ SkillsFuture payment is now available! (Course starts in {eligibility.daysUntilCourse} days)
                                                            </Text>
                                                        );
                                                    } else {
                                                        return (
                                                            <Text
                                                                fontSize={"sm"}
                                                                color="orange.500"
                                                                fontWeight="600"
                                                            >
                                                                ⏳ SkillsFuture payment will be available in {eligibility.daysLeft} days on {eligibility.skillsFutureAvailableDate.format("DD MMM YYYY (ddd)")}
                                                            </Text>
                                                        );
                                                    }
                                                })()}
                                                
                                                <Button 
                                                    size={"lg"} 
                                                    
                                                    letterSpacing="0.5px"
                                                    onClick={() => window.location.href = `/enrollment/${registrationID}`}
                                                    _textDecoration="none"
                                                    _outline="none"
                                                >
                                                    {getPaymentEligibility()?.canPayNow 
                                                        ? "Proceed to payment" 
                                                        : "Proceed to payment without SkillsFuture credit"
                                                    }
                                                </Button>
                                                
                                                {/* Reminder message for when SkillsFuture is not available */}
                                                {!getPaymentEligibility()?.canPayNow && (
                                                    <Text
                                                        fontSize={"sm"}
                                                        color="brand.500"
                                                        textAlign="center"
                                                        fontStyle="italic"
                                                    >
                                                        We will send out a reminder email once SkillsFuture credit payment is available. 
                                                        You may close the page now if you are intending to pay via SkillsFuture credit.
                                                    </Text>
                                                )}
                                            </VStack>
                                        )}
                                    </Text>
                                </motion.div>

                                {/* Course Details Section */}
                                <motion.div
                                    initial={{opacity: 0, y: 20}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{delay: 0.7, duration: 0.8}}
                                    style={{width: '100%'}}
                                >
                                    <Box
                                        p={{base: 3, md: 6}}
                                        bg="gray.50"
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor="brand.300"
                                    >
                                        <Text
                                            fontSize={mainFontSize}
                                            fontWeight="600"
                                            color="brand.700"
                                            mb={{base: 2, md: 4}}
                                        >
                                            Course Details
                                        </Text>
                                        <VStack spacing={{base: 2, md: 3}} align="start">
                                            <Text color="brand.500" fontSize={detailsFontSize}>
                                                <strong>Course:</strong> {registrationData.name}
                                            </Text>
                                            <Text color="brand.500" fontSize={detailsFontSize}>
                                                <strong>Start
                                                    Date:</strong> {moment(registrationData.start_date).format("DD MMM YYYY")} ({moment(registrationData.start_date).format("ddd")})
                                            </Text>
                                            <Text color="brand.500" fontSize={detailsFontSize}>
                                                <strong>End
                                                    Date:</strong> {moment(registrationData.end_date).format("DD MMM YYYY")} ({moment(registrationData.end_date).format("ddd")})
                                            </Text>
                                            <Text color="brand.500" fontSize={detailsFontSize}>
                                                <strong>Registration Type:</strong> {registrationData.registration_type}
                                            </Text>
                                        </VStack>
                                    </Box>
                                </motion.div>
                            </VStack>
                        </Box>
                    </motion.div>
                </Box>
            )}
        </Box>
    );
};

export default SuccessRegistration;